type JsonLike = null | boolean | number | string | JsonLike[] | { [key: string]: JsonLike }
const OMIT_FROM_SIGNATURE = Symbol("omit-from-signature")

/** Converts dot-path like "a.b.*.c" into trimmed path segments. */
function toPathSegments(path: string): string[] {
  return path
    .split(".")
    .map((segment) => segment.trim())
    .filter(Boolean)
}

/**
 * Returns only ignored paths that apply to the current segment and moves them one level deeper.
 * Supports wildcard segments (`*`).
 */
function getNestedIgnoredPaths(pathSegments: string[][], segment: string): string[][] {
  return pathSegments.flatMap(([currentSegment, ...rest]) => {
    if (currentSegment === segment || currentSegment === "*") {
      return [rest]
    }

    return []
  })
}

/**
 * Builds a stable JSON-like structure for hashing/stringification while omitting ignored paths.
 * Object keys are sorted to make output deterministic.
 *
 * Example (AppNode[]):
 * value = [{ id: "n1", position: { x: 10, y: 20 }, data: { label: "A", comments: "..." } }]
 * ignoredPaths = [["*", "position"], ["*", "data", "comments"]]
 * output = [{ data: { label: "A" }, id: "n1" }]
 *
 * Example (parsed EditorGraphDto.data / GraphDetails):
 * value = { Version: "1", Product: "X", description: "draft" }
 * ignoredPaths = []
 * output = { Product: "X", Version: "1", description: "draft" }
 */
function normaliseObjectForStableStringify(
  value: unknown,
  ignoredPaths: string[][],
): JsonLike | typeof OMIT_FROM_SIGNATURE {
  if (ignoredPaths.some((pathSegments) => pathSegments.length === 0)) {
    return OMIT_FROM_SIGNATURE
  }

  if (value == null || typeof value !== "object") {
    return value as JsonLike
  }

  if (Array.isArray(value)) {
    const normalisedArray: JsonLike[] = []

    for (const [index, item] of value.entries()) {
      const normalisedItem = normaliseObjectForStableStringify(
        item,
        getNestedIgnoredPaths(ignoredPaths, index.toString()),
      )

      if (normalisedItem === OMIT_FROM_SIGNATURE) {
        continue
      }

      normalisedArray.push(normalisedItem)
    }

    return normalisedArray
  }

  const normalisedObject: { [key: string]: JsonLike } = {}

  for (const key of Object.keys(value as Record<string, unknown>).sort()) {
    const nestedValue = (value as Record<string, unknown>)[key]
    if (nestedValue === undefined) continue

    const normalisedValue = normaliseObjectForStableStringify(
      nestedValue,
      getNestedIgnoredPaths(ignoredPaths, key),
    )

    if (normalisedValue === OMIT_FROM_SIGNATURE) {
      continue
    }

    normalisedObject[key] = normalisedValue
  }

  return normalisedObject
}

/**
 * Creates a deterministic signature string used to decide whether validation should rerun.
 * Ignored paths are excluded from the signature.
 */
export function createValidationTriggerSignature(value: unknown, ignoredPaths: string[]): string {
  const parsedIgnoredPaths = ignoredPaths
    .map((ignoredPath) => toPathSegments(ignoredPath))
    .filter((pathSegments) => pathSegments.length > 0)

  return JSON.stringify(normaliseObjectForStableStringify(value, parsedIgnoredPaths))
}
